import styles from 'css/src/components/billing-settings/PurchaseAnimationMinutesModal.module.scss';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Spinner } from '../common/ui/Spinner';

import { formatPrice } from './billing-utils';

import { format, parseISO } from 'date-fns';
import type { AnimationMinutesAddon, SubscriptionAddonPreview } from 'js/src/client';
import { RoleEnum } from 'js/src/client';
import { ApiService } from 'js/src/client/services.gen';
import { Toast } from 'js/src/components/common/Toast';
import {
    PrimaryButton,
    PrimaryTextButton,
    SecondaryButton,
    TertiaryButton,
} from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import type { PlainDropdownItem } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import Modal from 'js/src/components/common/ui/modal';
import { purchaseAnimationMinutes } from 'js/src/data/pricing/animation-minutes-operations';
import type {
    BillingInterval,
    GenXBillingContext,
} from 'js/src/data/usage-limit/usage-limit-selectors';
import {
    CHECKOUT_SESSION_SOURCE_ACCOUNT_BILLING,
    HUBSPOT_BOOK_CHAT_LINK,
} from 'js/src/libs/consts';
import RadioCheckedIcon from 'js/src/libs/custom_icons/RadioCheckedIcon';
import RadioUncheckedIcon from 'js/src/libs/custom_icons/RadioUncheckedIcon';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import PaymentService from 'js/src/libs/payment-service';
import { startSentryReplay } from 'js/src/libs/sentry';
import { capitalize } from 'js/src/libs/string-manipulation';

export const QUANTITY_OPTIONS_BY_INTERVAL = {
    monthly: [2, 5, 10],
    annually: [2, 5, 10],
} as const satisfies Record<BillingInterval, readonly number[]>;

export const CUSTOM_QUANTITY_OPTIONS_BY_INTERVAL = {
    monthly: [15, 20, 30, 60],
    annually: [15, 20, 30, 60],
} as const satisfies Record<BillingInterval, readonly number[]>;

export type AnimationMinutesContext = 'explainer' | 'clip';

// Each surface animates a different unit at a different average length, so the same purchased
// minutes translate to a different headline count of animations.
const CONTEXT_CONFIG: Record<
    AnimationMinutesContext,
    { unitNoun: string; avgSeconds: number; countByQuantity: Record<number, number> }
> = {
    explainer: { unitNoun: 'scene', avgSeconds: 8, countByQuantity: { 2: 15, 5: 36, 10: 75 } },
    clip: { unitNoun: 'clip', avgSeconds: 6, countByQuantity: { 2: 20, 5: 50, 10: 100 } },
};

type QuantitySelectData = { value: number };

type ModalStep = 'select' | 'review';

type SelectToggleProps = {
    selectedQuantity: number | null;
    onClick: VoidFunction;
    'aria-expanded'?: boolean | 'true' | 'false';
    ref?: React.Ref<HTMLButtonElement>;
};

const SelectToggle = React.memo(function SelectToggle({
    selectedQuantity,
    onClick,
    'aria-expanded': ariaExpanded,
    ref,
}: SelectToggleProps) {
    const isOpen = ariaExpanded === true || ariaExpanded === 'true';
    const hasValue = selectedQuantity !== null;
    return (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-expanded={ariaExpanded}
            className={styles['custom-dropdown-select']}
        >
            {hasValue ? (
                <span className="fw-semibold">{selectedQuantity} minutes</span>
            ) : (
                <span className="color-grey-700">Select</span>
            )}
            <i translate="no" className="material-symbols-outlined">
                {isOpen ? 'expand_less' : 'expand_more'}
            </i>
        </button>
    );
});

interface PurchaseAnimationMinutesModalProps {
    showing: boolean;
    onHidden: () => void;
    billingContext: GenXBillingContext | null;
    onSuccess: () => void;
    context?: AnimationMinutesContext;
    /**
     * If set, the modal opens directly at the review step with this quantity
     * pre-selected.
     */
    initialQuantity?: number;
    onBeforeRedirectToCheckout?: () => Promise<void>;
}

export const PurchaseAnimationMinutesModal: React.FC<PurchaseAnimationMinutesModalProps> = ({
    showing,
    onHidden,
    billingContext,
    onSuccess,
    context = 'explainer',
    initialQuantity,
    onBeforeRedirectToCheckout,
}) => {
    const billingInterval: BillingInterval = billingContext?.billingInterval ?? 'monthly';
    const currentQuantity = billingContext?.currentQuantity ?? 0;
    const planMinutes = billingContext?.planMinutes ?? 0;
    const [addon, setAddon] = useState<AnimationMinutesAddon | null>(null);
    const [addonLoading, setAddonLoading] = useState(false);
    const [addonError, setAddonError] = useState(false);
    const [selectedQuantity, setSelectedTierQuantity] = useState<number | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showCustomOptions, setShowCustomOptions] = useState(false);
    const [step, setStep] = useState<ModalStep>('select');
    const [preview, setPreview] = useState<SubscriptionAddonPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [cardDeclined, setCardDeclined] = useState(false);

    const isAdmin = SERVER.USER.role === RoleEnum.ADMIN;

    const isMonthly = billingInterval === 'monthly';
    const cycleSuffixShort = isMonthly ? '/mo' : '/year';
    const cycleSuffix = isMonthly ? '/month' : '/year';

    const quantityOptions = QUANTITY_OPTIONS_BY_INTERVAL[billingInterval];
    const customQuantityOptions = CUSTOM_QUANTITY_OPTIONS_BY_INTERVAL[billingInterval];

    const contextConfig = CONTEXT_CONFIG[context];
    const subtitleForQuantity = useCallback(
        (quantity: number): string | null => {
            const count = contextConfig.countByQuantity[quantity];
            if (count === undefined) {
                return null;
            }
            return `Animate ${count} more ${contextConfig.unitNoun}s a month`;
        },
        [contextConfig],
    );
    const animationCountFootnote = `${capitalize(contextConfig.unitNoun)} count based on ${contextConfig.unitNoun}s being an average of ${contextConfig.avgSeconds} seconds`;
    const billedCadence = isMonthly ? 'monthly' : 'yearly';
    const addonTermsText = `Add-ons are billed ${billedCadence} and stack on top of your current plan. Unused minutes expire at end of billing cycle.`;

    // Assumes no overlap between custom and fixed options
    const isCustomSelected = useMemo(
        () =>
            selectedQuantity !== null &&
            (customQuantityOptions as readonly number[]).includes(selectedQuantity),
        [selectedQuantity, customQuantityOptions],
    );

    useEffect(() => {
        if (!showing || !isAdmin || billingContext === null) {
            return;
        }
        startSentryReplay('purchase-animation-modal');
        const restoringFromCheckout = initialQuantity !== undefined && initialQuantity > 0;
        setSelectedTierQuantity(restoringFromCheckout ? initialQuantity : null);
        setShowCustomOptions(false);
        setStep(restoringFromCheckout ? 'review' : 'select');
        setPreview(null);
        setPreviewError(false);
        setCardDeclined(false);
        setAddon(null);
        setAddonError(false);
        setAddonLoading(true);
        ApiService.pricingPlansRetrieve()
            .then((response) => {
                const addons = isMonthly
                    ? response.data.monthly_addons
                    : response.data.annual_addons;
                // Only 1 add on
                const genXAddOn = addons[0];
                setAddon(genXAddOn);
            })
            .catch((err: unknown) => {
                ErrorReporter.capture(err);
                setAddonError(true);
                Toast.error('Could not load add-on options. Please try again.');
            })
            .finally(() => {
                setAddonLoading(false);
            });
    }, [showing, isMonthly, isAdmin, initialQuantity, billingContext]);

    const cyclePriceForQuantity = useCallback(
        (quantity: number): number => {
            if (!addon) {
                return 0;
            }
            return addon.unit_price_cents * quantity;
        },
        [addon],
    );

    const totalQuantity = useMemo(
        () => (selectedQuantity === null ? null : currentQuantity + selectedQuantity),
        [selectedQuantity, currentQuantity],
    );

    const fetchPreview = useCallback(async () => {
        if (!addon || totalQuantity === null) {
            return;
        }
        setPreviewLoading(true);
        setPreviewError(false);
        try {
            const response = await ApiService.pricingSubscriptionPreviewRetrieve({
                query: { genx_addon_name: addon.name, genx_addon_quantity: totalQuantity },
            });
            setPreview(response.data);
        } catch (err: unknown) {
            ErrorReporter.capture(err);
            setPreviewError(true);
            setPreview(null);
        }
        // Not in a `finally`: the React Compiler can't compile try/finally and skips the component.
        setPreviewLoading(false);
    }, [addon, totalQuantity]);

    useEffect(() => {
        if (step !== 'review') {
            return;
        }
        fetchPreview();
    }, [step, fetchPreview]);

    const currentAddonPriceText = useMemo(() => {
        if (currentQuantity <= 0) {
            return `-${cycleSuffix}`;
        }
        return `${formatPrice(cyclePriceForQuantity(currentQuantity))}${cycleSuffix}`;
    }, [currentQuantity, cyclePriceForQuantity, cycleSuffix]);

    const totalText = useMemo(() => {
        if (selectedQuantity === null || totalQuantity === null) {
            return `-${cycleSuffix}`;
        }
        const totalPriceText = formatPrice(cyclePriceForQuantity(totalQuantity)) + cycleSuffix;
        return `${totalQuantity} min • ${totalPriceText}`;
    }, [selectedQuantity, totalQuantity, cyclePriceForQuantity, cycleSuffix]);

    const headerText =
        currentQuantity > 0
            ? 'Add more animation minutes to your plan'
            : 'Add animation minutes to your plan';

    const totalCadence = isMonthly ? 'monthly' : 'yearly';
    const totalLabelText =
        currentQuantity > 0 ? `New ${totalCadence} total:` : `${capitalize(totalCadence)} total:`;

    const handleReview = useCallback(() => {
        if (selectedQuantity === null || !addon) {
            return;
        }
        setStep('review');
    }, [selectedQuantity, addon]);

    const handleBack = useCallback(() => {
        setStep('select');
    }, []);

    const handleConfirm = useCallback(async () => {
        if (selectedQuantity === null || !addon || totalQuantity === null) {
            return;
        }
        const purchaseType = currentQuantity > 0 ? 'upgrade' : 'new_subscription';
        analytics.track('GenX Pricing Confirm Clicked', {
            selectedQuantity,
            currentQuantity,
            totalQuantity,
            billingInterval,
            purchaseType,
        });
        setIsProcessing(true);
        setCardDeclined(false);
        const status = await purchaseAnimationMinutes(
            addon.name,
            totalQuantity,
            planMinutes + totalQuantity,
            preview?.proration_date,
        );
        setIsProcessing(false);

        // The 'GenX Animation Minutes Purchased' Mixpanel event is tracked server-side
        // in the Stripe webhook handler — the purchase can succeed without this poll
        // ever observing it (e.g. webhook slower than the ~15s polling window).
        if (status === 'ok') {
            onSuccess();
            return;
        }
        if (status === 'card_declined') {
            setCardDeclined(true);
            return;
        }
        Toast.error('Could not update your add-on. Please try again.');
    }, [
        selectedQuantity,
        addon,
        totalQuantity,
        currentQuantity,
        planMinutes,
        billingInterval,
        onSuccess,
        preview,
    ]);

    const handleUpdatePaymentMethod = useCallback(async () => {
        if (selectedQuantity === null) {
            return;
        }
        analytics.track('GenX Update Payment Method Clicked', {
            selectedQuantity,
            currentQuantity,
            billingInterval,
        });
        setIsProcessing(true);
        try {
            if (onBeforeRedirectToCheckout) {
                await onBeforeRedirectToCheckout();
            }
            const returnParams = new URLSearchParams(window.location.search);
            returnParams.set('aa_quantity', String(selectedQuantity));
            const returnPath = `${window.location.pathname}?${returnParams.toString()}${window.location.hash}`;
            const response = await ApiService.pricingPaymentMethodCheckoutUpdate({
                body: {
                    success_path: returnPath,
                    cancel_path: returnPath,
                    source: CHECKOUT_SESSION_SOURCE_ACCOUNT_BILLING,
                },
            });
            await PaymentService.redirectToCheckout(response.data.checkout_session_id);
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            Toast.error('Could not update payment method. Please try again.');
            setIsProcessing(false);
        }
    }, [selectedQuantity, currentQuantity, billingInterval, onBeforeRedirectToCheckout]);

    const handleMoreOptions = useCallback(() => {
        setShowCustomOptions(true);
    }, []);

    const selectQuantityAndTrack = useCallback(
        (quantity: number, isCustom: boolean) => {
            setSelectedTierQuantity(quantity);
            analytics.track('GenX Pricing Option Selected', {
                selectedQuantity: quantity,
                currentQuantity,
                billingInterval,
                isCustom,
            });
        },
        [currentQuantity, billingInterval],
    );

    const handleCustomSelect = useCallback(
        (onSelectData: QuantitySelectData) => {
            const value = onSelectData.value;
            if (typeof value === 'number') {
                selectQuantityAndTrack(value, true);
            }
        },
        [selectQuantityAndTrack],
    );

    const handleContactSales = useCallback(() => {
        Navigation.openNewTab(HUBSPOT_BOOK_CHAT_LINK);
    }, []);

    const toggleProps = useMemo(
        () => ({
            selectedQuantity: isCustomSelected ? selectedQuantity : null,
        }),
        [isCustomSelected, selectedQuantity],
    );

    const itemPropsList = useMemo((): readonly PlainDropdownItem<QuantitySelectData>[] => {
        return customQuantityOptions.map((quantity) => {
            const incrementCents = cyclePriceForQuantity(quantity);
            return {
                id: `quantity-${quantity}`,
                onSelectData: { value: quantity },
                className: styles['custom-dropdown-item'],
                children: (
                    <>
                        <span className="fw-semibold">{quantity} minutes</span>
                        <span className="color-grey-700">
                            {formatPrice(incrementCents)}
                            {cycleSuffixShort}
                        </span>
                    </>
                ),
            };
        });
    }, [customQuantityOptions, cyclePriceForQuantity, cycleSuffixShort]);

    const FooterMenu = useMemo(
        () =>
            React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
                function FooterMenu({ children, className, ...rest }, ref) {
                    return (
                        <div
                            ref={ref}
                            className={`${className ?? ''} ${styles['custom-dropdown-menu']}`}
                            {...rest}
                        >
                            {children}
                            <div className={styles['custom-dropdown-footer']}>
                                For more minutes,{' '}
                                <button
                                    type="button"
                                    className={styles['custom-dropdown-footer-link']}
                                    onClick={handleContactSales}
                                >
                                    contact sales
                                </button>
                            </div>
                        </div>
                    );
                },
            ),
        [handleContactSales],
    );

    const selectStepBody = (
        <>
            {currentQuantity > 0 && currentAddonPriceText && (
                <div className={`${styles['current-addon-box']} mb-l5-16 d-flex`}>
                    <div className="font-l5 font-l5--small me-l5-4">Current add-on:</div>
                    <div className="fw-semibold">
                        {' '}
                        {currentQuantity} min • {currentAddonPriceText}
                    </div>
                </div>
            )}

            <p className="mb-l5-16">Choose {billedCadence} animation minutes</p>

            <div
                className={`${styles['options-grid']} mb-l5-12`}
                role="radiogroup"
                aria-label="Animation minutes quantity"
            >
                {quantityOptions.map((quantity) => {
                    const isSelected = selectedQuantity === quantity;
                    const incrementCents = cyclePriceForQuantity(quantity);
                    const subtitle = subtitleForQuantity(quantity);
                    return (
                        <label
                            key={quantity}
                            className={`${styles['option-card']} ${
                                isSelected ? styles['option-card-selected'] : ''
                            }`}
                        >
                            <input
                                type="radio"
                                name="animation-minutes-quantity"
                                value={quantity}
                                checked={isSelected}
                                onChange={() => selectQuantityAndTrack(quantity, false)}
                                className="d-none"
                            />
                            <div className="flex-grow-1">
                                <div className="fw-semibold">{quantity} minutes</div>
                                {subtitle && <div className="color-grey-700">{subtitle}</div>}
                            </div>
                            <div className="color-grey-700 me-l5-8">
                                + {formatPrice(incrementCents)}
                                {cycleSuffixShort}
                            </div>
                            <div className={styles['option-card-radio']}>
                                {isSelected ? <RadioCheckedIcon /> : <RadioUncheckedIcon />}
                            </div>
                        </label>
                    );
                })}
            </div>

            <p className="color-grey-700 mb-l5-24">{animationCountFootnote}</p>

            {!showCustomOptions && (
                <div
                    className={`${styles['more-options-row']} d-flex justify-content-center align-items-center mb-l5-4`}
                >
                    <TertiaryButton text="More options" onClick={handleMoreOptions} />
                </div>
            )}

            {showCustomOptions && (
                <div
                    className={`${styles['custom-dropdown-card']} ${
                        isCustomSelected ? styles['custom-dropdown-card-selected'] : ''
                    } mb-l5-4`}
                >
                    <span className="fw-semibold">Select other amount</span>
                    <Dropdown<QuantitySelectData>
                        breakOverflow
                        ToggleComponent={SelectToggle}
                        toggleProps={toggleProps}
                        MenuComponent={FooterMenu}
                        items={itemPropsList}
                        onItemSelect={handleCustomSelect}
                        className={styles['custom-dropdown-half']}
                    />
                </div>
            )}
        </>
    );

    const selectStepFooter = (
        <div className="w-100">
            <div className={`${styles['total-box']} mb-l5-16`}>
                <span className="fw-semibold">{totalLabelText}</span>
                <span className="fw-semibold">{totalText}</span>
            </div>

            <div className={`${styles['info-row']} mb-l5-16`}>
                <i translate="no" className="material-symbols-outlined mt-l5-4">
                    info
                </i>
                <span>{addonTermsText}</span>
            </div>

            <div className="d-flex justify-content-end">
                <SecondaryButton
                    text="No thanks"
                    onClick={onHidden}
                    disabled={isProcessing}
                    className="me-l5-12"
                />
                <PrimaryButton
                    text="Review and confirm"
                    onClick={handleReview}
                    disabled={selectedQuantity === null || isProcessing || !addon}
                />
            </div>
        </div>
    );

    const isAddingToExisting = currentQuantity > 0;

    const addonCost = isAddingToExisting ? (
        <>
            <div className="d-flex justify-content-between align-items-center mb-l5-4">
                <span>
                    Current:{' '}
                    <span className="font-l5 font-l5--small">
                        {currentQuantity} mins{cycleSuffixShort}
                    </span>
                </span>
                <span className="font-l5 font-l5--regular--semibold">
                    {formatPrice(cyclePriceForQuantity(currentQuantity))} USD{cycleSuffixShort}
                </span>
            </div>
            <div className="d-flex justify-content-between align-items-center mb-l5-16">
                <span>
                    Adding:{' '}
                    <span className="font-l5 font-l5--small">
                        + {selectedQuantity ?? 0} mins{cycleSuffixShort}
                    </span>
                </span>
                <span className="font-l5 font-l5--regular--semibold">
                    + {formatPrice(cyclePriceForQuantity(selectedQuantity ?? 0))} USD
                    {cycleSuffixShort}
                </span>
            </div>
        </>
    ) : (
        <div className="d-flex justify-content-between align-items-center">
            <span className="font-l5 font-l5--small">
                {totalQuantity} minutes{cycleSuffix}
            </span>
            <span className="font-l5 font-l5--regular--semibold">
                {formatPrice(cyclePriceForQuantity(totalQuantity ?? 0))} USD
                {cycleSuffixShort}
            </span>
        </div>
    );

    const addonSummary = (
        <div className="mb-l5-16">
            Review and confirm your purchase
            <hr className="mx-l5-0 mt-l5-16 mb-l5-16" />
            <div className="font-l5 font-l5--subtitle-l mb-l5-4">Add-on</div>
            <div className="color-grey-700">{addonCost}</div>
            <hr className="mx-l5-0 mb-l5-16" />
            <div className="d-flex justify-content-between align-items-center">
                <span className="font-l5 font-l5--regular--semibold">
                    New {totalCadence} total: {totalQuantity ?? 0} mins{cycleSuffixShort}
                </span>
                <span className="font-l5 font-l5--regular--semibold">
                    {formatPrice(cyclePriceForQuantity(totalQuantity ?? 0))} USD
                    {cycleSuffixShort}
                </span>
            </div>
        </div>
    );

    const reviewStepBody = (
        <>
            {cardDeclined && (
                <Callout
                    className="mb-l5-16"
                    msgType={CALLOUT_TYPES.ERROR}
                    title="Your card was declined."
                    ctaText="Update payment information"
                    onCtaClick={handleUpdatePaymentMethod}
                />
            )}

            {addonSummary}

            <hr className="mx-l5-0 mb-l5-16" />

            {previewError && (
                <div
                    className={`${styles['preview-error-row']} mb-l5-16 d-flex justify-content-between align-items-center`}
                >
                    <span className="color-grey-700">
                        Error loading your prorated charge. Retry or continue. Your prorated charge
                        will be calculated on your next invoice.
                    </span>
                    <TertiaryButton text="Retry" onClick={fetchPreview} />
                </div>
            )}

            {!previewError && (
                <>
                    <p className="mb-l5-16">
                        Your charge today is prorated. You&apos;ll be billed{' '}
                        {formatPrice(cyclePriceForQuantity(totalQuantity ?? 0))}
                        {cycleSuffix}
                        {preview?.next_billing_date
                            ? ` starting ${format(parseISO(preview.next_billing_date), 'MMM d, yyyy')}`
                            : ''}
                        .
                    </p>
                    {previewLoading ? (
                        <div className="d-flex justify-content-center mb-l5-16">
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-l5-4">
                                <span className="font-l5 color-grey-600">Subtotal</span>
                                <span className="font-l5 color-grey-600">
                                    {previewLoading || !preview
                                        ? '—'
                                        : `${formatPrice(preview.subtotal_excluding_tax_cents)} USD`}
                                </span>
                            </div>
                            {preview && preview.tax_cents > 0 && (
                                <div className="d-flex justify-content-between align-items-center mb-l5-4">
                                    <span className="font-l5 color-grey-600">Tax</span>
                                    <span className="font-l5 color-grey-600">
                                        {formatPrice(preview.tax_cents)} USD
                                    </span>
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-l5-16">
                                <span className="font-l5 font-l5--regular--semibold">
                                    Charged today
                                </span>
                                <span className="font-l5 font-l5--regular--semibold">
                                    {!preview ? '—' : `${formatPrice(preview.total_cents)} USD`}
                                </span>
                            </div>
                        </>
                    )}
                </>
            )}

            <hr className="mx-l5-0 mb-l5-16" />

            <div className={`${styles['info-row']}`}>
                <i translate="no" className="material-symbols-outlined mt-l5-4">
                    info
                </i>
                <span>{addonTermsText}</span>
            </div>
        </>
    );

    const reviewStepFooter = (
        <div className="w-100 d-flex justify-content-between align-items-center">
            <PrimaryTextButton
                iconLeft="chevron_left"
                text="Back"
                onClick={handleBack}
                disabled={isProcessing}
            />
            <div className="d-flex">
                <SecondaryButton
                    text="Cancel"
                    onClick={onHidden}
                    disabled={isProcessing}
                    className="me-l5-12"
                />
                <PrimaryButton
                    text={isProcessing ? 'Processing...' : 'Confirm purchase'}
                    onClick={handleConfirm}
                    disabled={isProcessing || previewLoading || !addon}
                />
            </div>
        </div>
    );

    if (!isAdmin) {
        return (
            <Modal showing={showing} onHidden={onHidden} title={headerText}>
                <div className="py-l5-16">
                    <p className="color-grey-700 mb-l5-16">Contact admin to add more minutes</p>
                    <div className="d-flex justify-content-end">
                        <SecondaryButton text="Close" onClick={onHidden} />
                    </div>
                </div>
            </Modal>
        );
    }

    if (showing && billingContext === null) {
        // Billing data hasn't loaded yet. Show a spinner instead of step content
        // so we never render against stale or default-fallback values.
        return (
            <Modal showing={showing} onHidden={onHidden} title={headerText}>
                <div className="d-flex justify-content-center py-l5-32">
                    <Spinner />
                </div>
            </Modal>
        );
    }

    let bodyContent: React.ReactNode;
    let footerContent: React.ReactNode;
    if (addonLoading) {
        bodyContent = (
            <div className="d-flex justify-content-center py-l5-32">
                <Spinner />
            </div>
        );
    } else if (addonError || !addon) {
        bodyContent = (
            <div className="py-l5-16">
                <p className="color-grey-700 mb-l5-16">
                    Could not load animation minute options. Please close and try again.
                </p>
                <div className="d-flex justify-content-end">
                    <SecondaryButton text="Close" onClick={onHidden} />
                </div>
            </div>
        );
    } else if (step === 'select') {
        bodyContent = selectStepBody;
        footerContent = selectStepFooter;
    } else {
        bodyContent = reviewStepBody;
        footerContent = reviewStepFooter;
    }

    return (
        <Modal
            showing={showing}
            onHidden={onHidden}
            title={headerText}
            scrollable
            footer={footerContent}
            showFooterDivider
        >
            {bodyContent}
        </Modal>
    );
};
